import { completeRecordingUpload, createRecording, uploadRecordingChunk } from './recordingApi';

const DATABASE = 'rami-recording-queue';
const VERSION = 1;
const CHUNKS = 'chunks';
const KEYS = 'keys';

interface StoredChunk {
  key: string;
  recordingId: string;
  channel: 'owner' | 'rami';
  sequence: number;
  ciphertext: ArrayBuffer;
  iv: string;
  checksum: string;
  startMs: number;
  endMs: number;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(CHUNKS)) database.createObjectStore(CHUNKS, { keyPath: 'key' });
      if (!database.objectStoreNames.contains(KEYS)) database.createObjectStore(KEYS);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('تعذر فتح طابور التسجيل.'));
  });
}

function transactionResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('تعذر حفظ التسجيل محليًا.'));
  });
}

async function recordingKey(database: IDBDatabase, recordingId: string): Promise<CryptoKey> {
  const current = await transactionResult(database.transaction(KEYS, 'readonly').objectStore(KEYS).get(recordingId)) as CryptoKey | undefined;
  if (current) return current;
  throw new Error('مفتاح التسجيل المحلي غير موجود.');
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function hex(bytes: Uint8Array): string {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function prepareRecording(callId: string): Promise<string> {
  const database = await openDatabase();
  const recordingId = crypto.randomUUID();
  try {
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    const exported = await crypto.subtle.exportKey('raw', key);
    await transactionResult(database.transaction(KEYS, 'readwrite').objectStore(KEYS).put(key, recordingId));
    await createRecording({
      id: recordingId,
      callId,
      contentKeyBase64: base64(new Uint8Array(exported)),
      keyVersion: 1
    });
    return recordingId;
  } finally {
    database.close();
  }
}

export async function enqueueChunk(input: {
  recordingId: string;
  channel: 'owner' | 'rami';
  sequence: number;
  blob: Blob;
  startMs: number;
  endMs: number;
}): Promise<void> {
  const database = await openDatabase();
  try {
    const key = await recordingKey(database, input.recordingId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plaintext = await input.blob.arrayBuffer();
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
    const checksum = hex(new Uint8Array(await crypto.subtle.digest('SHA-256', ciphertext)));
    const stored: StoredChunk = {
      key: `${input.recordingId}:${input.channel}:${input.sequence}`,
      recordingId: input.recordingId,
      channel: input.channel,
      sequence: input.sequence,
      ciphertext,
      iv: base64(iv),
      checksum,
      startMs: input.startMs,
      endMs: input.endMs
    };
    await transactionResult(database.transaction(CHUNKS, 'readwrite').objectStore(CHUNKS).put(stored));
  } finally {
    database.close();
  }
}

export async function flushChunks(recordingId: string): Promise<number> {
  const database = await openDatabase();
  try {
    const all = await transactionResult(database.transaction(CHUNKS, 'readonly').objectStore(CHUNKS).getAll()) as StoredChunk[];
    const pending = all.filter((item) => item.recordingId === recordingId).sort((a, b) => a.sequence - b.sequence);
    let uploaded = 0;
    for (const chunk of pending) {
      const result = await uploadRecordingChunk({
        ...chunk,
        ciphertextBase64: base64(new Uint8Array(chunk.ciphertext)),
        keyVersion: 1
      });
      if (!result.acknowledged) throw new Error('لم يؤكد الخادم حفظ قطعة التسجيل.');
      await transactionResult(database.transaction(CHUNKS, 'readwrite').objectStore(CHUNKS).delete(chunk.key));
      uploaded += 1;
    }
    return uploaded;
  } finally {
    database.close();
  }
}

export async function completeQueuedRecording(
  recordingId: string,
  expectedOwnerChunks: number,
  expectedRamiChunks = 0
): Promise<void> {
  await completeRecordingUpload(recordingId, expectedOwnerChunks, expectedRamiChunks);
}

export async function pendingChunkCount(recordingId: string): Promise<number> {
  const database = await openDatabase();
  try {
    const all = await transactionResult(database.transaction(CHUNKS, 'readonly').objectStore(CHUNKS).getAll()) as StoredChunk[];
    return all.filter((item) => item.recordingId === recordingId).length;
  } finally {
    database.close();
  }
}
